// Fetch wrapper for every backend call (erp-core and bff-business).
//
//   api.get('/erp/accounts', { period: '2026-09' })    → GET  /api/erp/accounts?period=2026-09
//   api.post('/business/v1/business/bookings', body)    → POST /api/business/v1/business/bookings
//   erpApi.get('/me')  ≡ api.get('/erp/me')             businessApi.get('/v1/config') ≡ api.get('/business/v1/config')
//   profileApi.get('/v1/business/memberships') ≡ api.get('/profile/v1/business/memberships')  (online only)
//
// Every request carries the bearer + X-Business-Id (active business). Errors
// are RFC 9457 Problem Details (ADR-0002) parsed into a typed ApiError whose
// `code` is the stable contract (e.g. 'period_closed'), `detail` the Arabic
// user-facing sentence, `extensions` every non-standard member.
//
// 401 → ONE silent refresh + retry; if that fails the session LOCKS instead of
// signing out, so nothing on screen is lost — the lock screen re-authenticates
// with the PIN. erp-core's node sessions answer `401 session_locked` once idle
// past LOCAL_IDLE_LOCK_MINUTES: that is the idle lock (reason 'idle'); any other
// 401 locks with reason 'expired'.
// Network failure (no HTTP response) → ApiError{status:0, code:'network'} and
// the connectivity store flips to offline.
// 503 with problem code "maintenance" → the maintenance store turns on (return
// time from the problem's `resumes_at` or Retry-After); the app root shows
// system/maintenance (lib/maintenance.ts).
// erp-core calls also carry X-Branch-Id when a branch is active (erp.branches).

import { API_BASE } from './env';
import { onlineStore } from './online';
import { lockStore } from './idle-lock';
import { refreshSession, sessionStore } from './auth';
import { MAINTENANCE_CODE, maintenanceStore, retryAfterToIso } from './maintenance';
import { BUSINESS_REQUIRED, noBusinessStore } from './no-business';

export interface ProblemFieldError {
  field?: string;
  code?: string;
  message?: string;
}

const STANDARD_MEMBERS = new Set(['type', 'title', 'status', 'detail', 'instance', 'code', 'errors']);

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: string;
  readonly title?: string;
  readonly type?: string;
  readonly instance?: string;
  readonly extensions: Record<string, unknown>;
  readonly errors?: ProblemFieldError[];

  constructor(init: {
    status: number;
    code: string;
    detail?: string;
    title?: string;
    type?: string;
    instance?: string;
    extensions?: Record<string, unknown>;
    errors?: ProblemFieldError[];
  }) {
    super(init.detail || init.title || `HTTP ${init.status}`);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.detail = init.detail;
    this.title = init.title;
    this.type = init.type;
    this.instance = init.instance;
    this.extensions = init.extensions ?? {};
    this.errors = init.errors;
  }

  /** No HTTP response at all (offline / DNS / refused). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
  /** 409 — the record changed elsewhere or the period is closed. Never auto-retry. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** erp-core's 409 problem code for a write dated in a closed period (BRIEF rule 8). */
export const PERIOD_CLOSED = 'period_closed';

/**
 * 409 `period_closed` — every such refusal opens the ONE shell-level «فترة
 * مقفلة» dialog (accounts/close LockedPeriodHost, mounted by AppShell; the
 * QueryClient's MutationCache routes every mutation to it — App.tsx).
 */
export function isPeriodClosed(e: unknown): e is ApiError {
  return isApiError(e) && e.status === 409 && e.code === PERIOD_CLOSED;
}

/** Parse an RFC 9457 body (already JSON-decoded) into an ApiError. */
export function problemToError(status: number, body: unknown): ApiError {
  const p = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const extensions: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (!STANDARD_MEMBERS.has(k)) extensions[k] = v;
  if (p.extensions && typeof p.extensions === 'object') Object.assign(extensions, p.extensions as Record<string, unknown>);
  const type = typeof p.type === 'string' ? p.type : undefined;
  const code =
    (typeof p.code === 'string' && p.code) ||
    (type && type !== 'about:blank' ? type.replace(/^.*\//, '') : '') ||
    defaultCode(status);
  return new ApiError({
    status,
    code,
    type,
    title: typeof p.title === 'string' ? p.title : undefined,
    detail: typeof p.detail === 'string' ? p.detail : undefined,
    instance: typeof p.instance === 'string' ? p.instance : undefined,
    extensions,
    errors: Array.isArray(p.errors) ? (p.errors as ProblemFieldError[]) : undefined,
  });
}

function defaultCode(status: number): string {
  switch (status) {
    case 400: return 'bad_request';
    case 401: return 'unauthorized';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 409: return 'conflict';
    case 422: return 'validation';
    case 429: return 'rate_limited';
    default: return status >= 500 ? 'server_error' : 'http_error';
  }
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  method?: string;
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Override the active business for this call (null = send no X-Business-Id). */
  businessId?: string | null;
  /** Override the active branch for this erp-core call (null = send no X-Branch-Id → main). */
  branchId?: string | null;
  /**
   * false = a 401 is just an error for this call: no silent refresh, no lock.
   * For optional reads from services that may not trust this session yet.
   */
  authRecovery?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const rel = path.startsWith('/') ? path : `/${path}`;
  let url = `${API_BASE}${rel}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v != null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  return url;
}

/** erp-core paths (the only service that reads X-Branch-Id). */
function isErpPath(path: string): boolean {
  return path === '/erp' || path.startsWith('/erp/');
}

function headersFor(path: string, opts: RequestOptions): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/json, application/problem+json' };
  const token = sessionStore.getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const biz = opts.businessId === undefined ? sessionStore.getBusinessId() : opts.businessId;
  if (biz) h['X-Business-Id'] = biz;
  if (isErpPath(path)) {
    const branch = opts.branchId === undefined ? sessionStore.getBranchId() : opts.branchId;
    if (branch) h['X-Branch-Id'] = branch;
  }
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body != null && !isForm) h['Content-Type'] = 'application/json';
  return { ...h, ...opts.headers };
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  try {
    const res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers: headersFor(path.startsWith('/') ? path : `/${path}`, opts),
      body: opts.body == null ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
    onlineStore.reportSuccess();
    return res;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    onlineStore.reportNetworkFailure();
    throw new ApiError({ status: 0, code: 'network', detail: 'لم تنجح المحاولة.' });
  }
}

/** First path segment: 'erp' | 'business' | 'profile' (the service behind the proxy). */
function serviceOf(path: string): string {
  return path.replace(/^\//, '').split(/[/?]/)[0] || 'api';
}

async function decode<T>(res: Response, service = 'api'): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    const err = problemToError(res.status, body);
    // Signed in but in no business at all (deleted / removed): the shell
    // routes to the onboarding's create-business step (lib/no-business.ts).
    if (res.status === 400 && err.code === BUSINESS_REQUIRED && sessionStore.isAuthenticated()) noBusinessStore.report();
    if (res.status === 503 && err.code === MAINTENANCE_CODE) {
      const resumes = typeof err.extensions.resumes_at === 'string' ? err.extensions.resumes_at : retryAfterToIso(res.headers.get('Retry-After'));
      maintenanceStore.report({ active: true, resumesAt: resumes }, `api:${service}`);
    } else if (maintenanceStore.heldByApi(service) && res.status !== 503) {
      // Any other HTTP answer: that service is back (out of maintenance).
      maintenanceStore.report({ active: false, resumesAt: null }, `api:${service}`);
    }
    throw err;
  }
  // A service that answered normally is out of maintenance.
  if (maintenanceStore.heldByApi(service)) maintenanceStore.report({ active: false, resumesAt: null }, `api:${service}`);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Core request. Throws ApiError. */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts);
  if (res.status === 401 && opts.authRecovery !== false && sessionStore.isAuthenticated()) {
    if ((await problemCode(res)) === 'session_locked') {
      lockStore.lock('idle');
      return decode<T>(res, serviceOf(path));
    }
    if (await refreshSession()) {
      res = await send(path, opts);
    }
    if (res.status === 401) {
      lockStore.lock((await problemCode(res)) === 'session_locked' ? 'idle' : 'expired');
    }
  }
  // A stored active business this person is not a member of (a previous
  // person's choice on this browser, or a membership removed meanwhile): drop
  // it and let the server pick its default — ONE retry, only when the header
  // came from the store (an explicit opts.businessId is the caller's business).
  if (res.status === 403 && opts.businessId === undefined && sessionStore.getBusinessId() && (await problemCode(res)) === 'not_a_member') {
    sessionStore.setBusinessId(null);
    sessionStore.setBranchId(null);
    res = await send(path, opts);
  }
  return decode<T>(res, serviceOf(path));
}

/** The problem `code` of a response without consuming its body. */
async function problemCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: unknown };
    return typeof body?.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

function verbs(prefix: string) {
  return {
    get: <T>(path: string, query?: Query, init?: Omit<RequestOptions, 'query' | 'method'>) =>
      apiRequest<T>(prefix + path, { ...init, query }),
    post: <T>(path: string, body?: unknown, init?: Omit<RequestOptions, 'body' | 'method'>) =>
      apiRequest<T>(prefix + path, { ...init, method: 'POST', body }),
    put: <T>(path: string, body?: unknown, init?: Omit<RequestOptions, 'body' | 'method'>) =>
      apiRequest<T>(prefix + path, { ...init, method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown, init?: Omit<RequestOptions, 'body' | 'method'>) =>
      apiRequest<T>(prefix + path, { ...init, method: 'PATCH', body }),
    delete: <T>(path: string, init?: Omit<RequestOptions, 'method'>) => apiRequest<T>(prefix + path, { ...init, method: 'DELETE' }),
  };
}

/** Paths include the service prefix: api.get('/erp/me'), api.get('/business/v1/config'). */
export const api = verbs('');
/** erp-core (/api/erp/* → /v1/erp/*): erpApi.get('/accounts'). */
export const erpApi = verbs('/erp');
/**
 * The ONE appointment-book client (BRIEF owner addition B): bff-business online,
 * erp-core localbook on a node — identical paths and JSON shapes, so screens
 * never know which: businessApi.get('/v1/business/bookings', { date }).
 */
export const businessApi = verbs('/business');
/**
 * ihjizli-profile, online only (/api/profile/* → profile, prefix stripped by
 * nginx / the Vite proxy). Used for the business switcher's memberships.
 */
export const profileApi = verbs('/profile');
/**
 * ihjizli-booking, online only (/api/booking/* → booking, prefix stripped).
 * The web onboarding writes a new business's weekly hours + default
 * scheduling mode here, exactly as the business app does (direct, bearer).
 */
export const bookingApi = verbs('/booking');
